import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _forEach from 'lodash/forEach';
import siteIcon from './icons/site';
import currentLocationIcon from './icons/currentLocation';
import historyIcon from './icons/history';
import classnames from 'classnames';
import algoliasearch from 'algoliasearch/lite';
import style from './styles/main';
import defaultStyle from './styles/default';
import spinnerStyle from './styles/spinner';
import ReactSVG from 'react-svg';
import shortid from 'shortid';
import closeIcon from '../../../assets/images/close.svg';
import { PREV_INPUT_TAG, SEARCH_HISTORY_TAG } from './constants';

class AlgoliaPlaces extends Component {
  constructor(props) {
    super(props);

    this.state = {
      closeIconVisible: ''
    };

    this.generateInputKey();
    this.setStaticVariables();
    this.bindFunctions();
  }

  componentDidMount() {
    this.initEverything(this.props);
  }

  componentDidUpdate() {
    if (!this.initDone) this.initEverything(this.props);
  }

  stateUpdated(state) {
    return this.state !== state;
  }

  setDynamicVariables(props) {
    const { fixtures } = props;
    this.fixturesPresent = fixtures && fixtures.length > 0;
  }

  shouldComponentUpdate(props, state) {
    this.setDynamicVariables(props);
    this.handleIndependentProps(props);

    return (
      this.stateUpdated(state) ||
      this.lightPropsUpdated(props) ||
      this.handleHeavyProps(props)
    );
  }

  componentWillUnmount() {
    this.destroyInstance();
  }

  generateInputKey() {
    this.inputKey = shortid.generate();
  }

  // noinspection DuplicatedCode
  bindFunctions() {
    this.setInputRef = this.setInputRef.bind(this);
    this.setRef = this.setRef.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.searchForFixtures = this.searchForFixtures.bind(this);
    this.searchForCurrentLocation = this.searchForCurrentLocation.bind(this);
    this.getFixturesSuggestion = this.getFixturesSuggestion.bind(this);
    this.getFixturesHeaderTemplate = this.getFixturesHeaderTemplate.bind(this);
    this.getCurrentLocationSuggestion = this.getCurrentLocationSuggestion.bind(this);
    this.updateInputValue = this.updateInputValue.bind(this);
    this.handleIconClose = this.handleIconClose.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleIconCloseMouseDown = this.handleIconCloseMouseDown.bind(this);
    this.handleIconCloseMouseUp = this.handleIconCloseMouseUp.bind(this);
    this.searchForHistory = this.searchForHistory.bind(this);
    this.compareFixtureKey = this.compareFixtureKey.bind(this);
    this.compareAddressKey = this.compareAddressKey.bind(this);
    this.getHistorySuggestion = this.getHistorySuggestion.bind(this);
    this.getHistoryHeaderTemplate = this.getHistoryHeaderTemplate.bind(this);
    this.getAddressHeaderTemplate = this.getAddressHeaderTemplate.bind(this);
    this.setResultsToEmpty = this.setResultsToEmpty.bind(this);
    this.setResultsToShown = this.setResultsToShown.bind(this);
  }

  setStaticVariables() {
    this.containerClass = 'react-algolia-places';
    this.searchHistoryCallback = () => undefined;
    this.historyItemsLoadedFromLocalStorage = false;
    this.historyItems = [];
  }

  setVariables(props) {
    const { aaOptions, fixtures, fuseOptions } = props;
    const { includeMatches } = fuseOptions || {};
    const { cssClasses } = aaOptions || {};
    const { prefix, root } = cssClasses || {};
    const innerClass = 'algolia-main';

    const includeMatchesUndefined = includeMatches === undefined;
    const includeMatchesDefault = true;

    this.currentValue = '';
    this.prefix = prefix || 'ap';
    this.rootClass = root ? classnames(root, innerClass) : innerClass;
    this.maxPatternLength = 32; // fixtures query maximum length
    this.visiblePlacesClass = 'visible'; // default mod class
    this.spinnerClass = 'spinner-active'; // class to display spinner
    this.fixturesPresent = fixtures ? fixtures.length > 0 : false;

    this.initHistoryFromStorage(props);

    this.includeMatches = includeMatchesUndefined
      ? includeMatchesDefault
      : !!includeMatches;
  }

  setFuseOptions({ fuseOptions, fixturesSearchKeys }) {
    this.fuseOptions = {
      shouldSort: true,
      threshold: 0.5,
      location: 0,
      distance: 100,
      keys: fixturesSearchKeys,
      minMatchCharLength: 1,
      ...fuseOptions,
      includeMatches: this.includeMatches
    };
  }

  setInputRef(ref) {
    this.autocompleteElem = ref;
  }

  getAddressHeaderTemplate() {
    if (this.fixturesPresent) {
      const { addressesTitle } = this.props;
      return `<div class="${this.prefix}-header">${addressesTitle}</div>`;
    }
  }

  // fix search with 0 results issue
  replacePlacesSource(placesDataSet) {
    return (query, cb) => {
      query = query.trim();

      if (query) {
        const trojanCallback = (data) => {
          if (data.length > 0) this.searchHistoryCallback([]);
          else this.showSearchHistory();
          cb(data);
        };

        placesDataSet.source(query, trojanCallback);
      } else {
        cb([]);
        this.showSearchHistory();
      }
    };
  }

  showSearchHistory() {
    const { searchHistoryEnabled } = this.props;
    if (searchHistoryEnabled) this.searchHistoryCallback(this.historyItems);
    else this.searchHistoryCallback([]);
  }

  getAutocompleteDataset(props) {
    const placesDataSet = this.createDataset({
      algoliasearch: algoliasearch,
      style: false,
      templates: {
        header: this.getAddressHeaderTemplate
      },
      onError: props.onError,
      onRateLimitReached: props.onLimit,
      language: props.language,
      ...props.placesOptions
    });

    return {
      ...placesDataSet,
      source: this.replacePlacesSource(placesDataSet)
    };
  }

  getHighlightedItem(suggestion, key) {
    if (this.includeMatches) {
      const { _matches: matches } = suggestion;

      const match = _find(matches, (match) => match && match.key === key);

      if (match) {
        const strArray = [];
        const indices = match.indices || [];
        const value = match.value || '';
        let prevIndex = 0;
        const { length } = value;

        indices.forEach((item) => {
          const itemStart = item[0];
          const itemEnd = item[1] + 1;

          if (itemStart) strArray.push(value.substring(prevIndex, itemStart));
          strArray.push('<em>');
          strArray.push(value.substring(itemStart, itemEnd));
          strArray.push('</em>');
          prevIndex = itemEnd;
        });

        if (length !== prevIndex) strArray.push(value.substring(prevIndex, length));

        return strArray.join('');
      }
    }

    return _get(suggestion, key);
  }

  setPlacesVisibility(valuePresent, setTimeout, restoreTimeout) {
    this.setClass(valuePresent, this.visiblePlacesClass, setTimeout, restoreTimeout);
  }

  getFixturesSuggestionInternal(suggestion, replaceIcon) {
    const { fixturesDisplayKey, fixturesInfoKey } = this.props;
    const nameItem = this.getHighlightedItem(suggestion, fixturesDisplayKey);
    const infoItem = this.getHighlightedItem(suggestion, fixturesInfoKey);

    const icon =
      replaceIcon || `<span class="${this.prefix}-fixture-icon">${siteIcon}</span>`;

    const name = `<span class="${this.prefix}-name">${nameItem}</span>`;

    const info = infoItem
      ? `<span class="${this.prefix}-address">${infoItem}</span>`
      : '';

    return icon + name + info;
  }

  getFixturesSuggestion(suggestion) {
    return this.getFixturesSuggestionInternal(suggestion);
  }

  getCurrentLocationSuggestion(suggestion) {
    const { value } = suggestion;

    const icon = `<span class="${this.prefix}-suggestion-icon current-location-icon">${currentLocationIcon}</span>`;
    const spinner = `<span class="${this.prefix}-suggestion-icon spinner-container"><span class="location-spinner"/></span>`;
    const name = `<span class="${this.prefix}-name">${value}</span>`;

    return icon + spinner + name;
  }

  getAddressSuggestion(suggestion, icon) {
    const { name: suggestionName, country, city, administrative } = suggestion;

    const infoMessage =
      this.addStr(city) + this.addStr(administrative) + this.addStr(country, '');

    const name = `<span class="${this.prefix}-name">${suggestionName}</span>`;
    const info = `<span class="${this.prefix}-address">${infoMessage}</span>`;

    return icon + name + info;
  }

  getHistorySuggestion(suggestion) {
    const { fixturesDatasetName } = this.props;
    const { _dataset } = suggestion;
    const icon = `<span class="${this.prefix}-suggestion-icon search-history-icon">${historyIcon}</span>`;

    if (_dataset === fixturesDatasetName) {
      return this.getFixturesSuggestionInternal(suggestion, icon);
    } else return this.getAddressSuggestion(suggestion, icon);
  }

  getFixturesHeaderTemplate() {
    return `<div class="${this.prefix}-header">${this.props.fixturesTitle}</div>`;
  }

  getFixturesDataset(props) {
    return {
      source: this.searchForFixtures,
      name: props.fixturesDatasetName,
      displayKey: props.fixturesDisplayKey,
      templates: {
        header: this.getFixturesHeaderTemplate,
        suggestion: this.getFixturesSuggestion
      }
    };
  }

  getCurrentLocationDataset(props) {
    return {
      source: this.searchForCurrentLocation,
      name: props.currentLocationDatasetName,
      displayKey: PREV_INPUT_TAG,
      templates: {
        suggestion: this.getCurrentLocationSuggestion
      }
    };
  }

  getHistoryHeaderTemplate() {
    if (this.fixturesPresent) {
      const { historyTitle } = this.props;
      return `<div class="${this.prefix}-header">${historyTitle}</div>`;
    }
  }

  getHistoryDataset(props) {
    return {
      source: this.searchForHistory,
      name: props.historyDatasetName,
      displayKey: SEARCH_HISTORY_TAG,
      templates: {
        header: this.getHistoryHeaderTemplate,
        suggestion: this.getHistorySuggestion
      }
    };
  }

  getAutocompleteInstance(props, datasets) {
    const { aaOptions } = props;
    const { cssClasses } = aaOptions || {};

    return this.autocomplete(
      this.autocompleteElem,
      {
        hint: false,
        openOnFocus: true,
        autoselect: false,
        tabAutocomplete: false,
        debug: false,
        ...props.aaOptions,
        cssClasses: {
          ...cssClasses,
          prefix: this.prefix,
          root: this.rootClass
        },
        minLength: 0
      },
      datasets
    );
  }

  forwardInstance(props, instance) {
    if (props.aaInstance) props.aaInstance(instance);
  }

  forwardSetter(props) {
    if (props.getValueSetter) props.getValueSetter(this.updateInputValue);
  }

  // handle 'includeMatches' and 'maxFixtures' props
  handleFuseResults(results) {
    const { maxFixtures } = this.props;

    return results.reduce((sum, data, index) => {
      const item = { ...data.item };
      if (this.includeMatches) item._matches = data.matches;
      if (!maxFixtures || index < maxFixtures) sum.push(item);
      return sum;
    }, []);
  }

  searchForFixtures(query, callback) {
    if (this.fuse) {
      query = query.substring(0, this.maxPatternLength);
      query = query.trim();
      if (query) {
        const results = this.fuse.search(query);
        callback(this.handleFuseResults(results));
      } else {
        let { fixtures } = this.props;
        const { initialNumberOfFixtures } = this.props;
        if (initialNumberOfFixtures)
          fixtures = fixtures.slice(0, initialNumberOfFixtures);
        callback(fixtures);
      }
    } else callback([]);
  }

  searchForCurrentLocation(query, callback) {
    const { showCurrentLocationButton, currentLocationLabel } = this.props;

    if (showCurrentLocationButton) {
      const result = {
        value: currentLocationLabel,
        [PREV_INPUT_TAG]: this.autocompleteElem.value,
        _currentLocation: true
      };

      callback([result]);
    } else callback([]);
  }

  searchForHistory(query, callback) {
    this.searchHistoryCallback = callback;
  }

  initFuse({ fixtures }) {
    if (this.fixturesPresent) {
      this.fuse = new Fuse(fixtures, this.fuseOptions);
      this.prevFixtures = _cloneDeep(fixtures);
    } else {
      this.fuse = null;
      this.prevFixtures = null; // reset prev fixtures
    }
  }

  initLibraries(props) {
    const { appId, apiKey } = props.placesOptions || {};

    if (!this.createDataset)
      this.createDataset = require('places.js/autocompleteDataset');
    if (!this.placesInstance)
      this.placesInstance = algoliasearch.initPlaces(appId, apiKey);
    if (!this.autocomplete) this.autocomplete = require('autocomplete.js');
  }

  createAaInstance(props) {
    const placesDataset = this.getAutocompleteDataset(props);
    const fixturesDataset = this.getFixturesDataset(props);
    const currentLocationDataset = this.getCurrentLocationDataset(props);
    const historyDataset = this.getHistoryDataset(props);

    this.aaInstance = this.getAutocompleteInstance(props, [
      currentLocationDataset,
      fixturesDataset,
      historyDataset,
      placesDataset
    ]);
  }

  initEverything(props) {
    this.initLibraries(props);
    this.setVariables(props);
    this.setFuseOptions(props);
    this.initFuse(props);
    this.createAaInstance(props);
    this.forwardInstance(props, this.aaInstance);
    this.forwardSetter(props);
    this.setInitialValue(props.initialValue);
    this.connectEvents(props);

    this.initDone = true;
  }

  handleClose() {
    this.setResultsToEmpty();
    this.autocompleteElem && this.autocompleteElem.blur();
  }

  setResultsToEmpty() {
    this.setPlacesVisibility(false);
  }

  setResultsToShown() {
    this.setPlacesVisibility(true);
  }

  connectEvents(props) {
    // can be string or function
    const autocompleteListeners = {
      'autocomplete:cursorchanged': 'onCursorChanged',
      'autocomplete:selected': this.handleChange,
      'autocomplete:closed': this.handleClose,
      'autocomplete:empty': this.setResultsToEmpty,
      'autocomplete:shown': this.setResultsToShown
    };

    for (const event in autocompleteListeners) {
      const listener = autocompleteListeners[event];
      const func = typeof listener === 'string' ? props[listener] : listener;
      if (typeof func === 'function') this.aaInstance.on(event, func);
    }
  }

  setInitialValue(value) {
    this.updateInputValue(value);
  }

  fireSelectCallback(suggestion) {
    const { onSelect } = this.props;
    if (typeof onSelect === 'function') onSelect(suggestion);
  }

  excludeMatches(suggestion) {
    if (this.includeMatches) {
      suggestion = { ...suggestion };
      delete suggestion._matches;
    }
    return suggestion;
  }

  handleChange(event, suggestion, dataset) {
    const {
      fixturesDatasetName,
      currentLocationDatasetName,
      fixturesDisplayKey
    } = this.props;

    dataset = this.updateHistoryDataSet(suggestion) || dataset;
    suggestion = this.clearHistorySuggestion(suggestion);

    const { value } = suggestion;
    const fixturesValue = suggestion[fixturesDisplayKey];
    const currentLocation = dataset === currentLocationDatasetName;
    const isFixtures = dataset === fixturesDatasetName;

    if (this.handleCurrentLocationSelect(event, suggestion, dataset)) return;

    this.handleSaveHistory(suggestion, dataset);

    if (isFixtures) {
      this.updateInternalValue(fixturesValue);
      suggestion = this.excludeMatches(suggestion);
    } else if (currentLocation) {
      this.updateInputValue(value);
    } else this.updateInternalValue(value);

    this.fireSelectCallback(suggestion);
  }

  clearHistorySuggestion(suggestion) {
    suggestion = { ...suggestion };
    delete suggestion._dataset;
    delete suggestion._isHistory;
    delete suggestion[SEARCH_HISTORY_TAG];
    return suggestion;
  }

  updateHistoryDataSet(suggestion) {
    const { _dataset } = suggestion;
    return _dataset;
  }

  updateInternalValue(value) {
    this.currentValue = value;
    this.handleValueChange(value);
  }

  handleSaveHistory(suggestion, dataset) {
    const {
      maxHistoryItems,
      searchHistoryId,
      searchHistoryEnabled,
      fixturesDatasetName,
      fixturesDisplayKey
    } = this.props;

    if (!searchHistoryEnabled || !window) return;
    if (suggestion._isHistory) return;

    const isFixtures = dataset === fixturesDatasetName;
    const value = isFixtures ? suggestion[fixturesDisplayKey] : suggestion.value;

    suggestion = {
      _dataset: dataset,
      _isHistory: true,
      [SEARCH_HISTORY_TAG]: value,
      ...suggestion
    };

    this.deleteHistoryKey(suggestion, isFixtures);

    this.historyItems.unshift(suggestion);
    if (this.historyItems.length > maxHistoryItems) this.historyItems.pop();
    const packedItems = JSON.stringify(this.historyItems);
    window.localStorage.setItem(searchHistoryId, packedItems);
  }

  deleteHistoryKey(suggestion, isFixtures) {
    const compareFunction = isFixtures ? this.compareFixtureKey : this.compareAddressKey;

    _forEach(this.historyItems, (item, index) => {
      if (compareFunction(item, suggestion, index)) return false; // exit loop when item discovered
    });
  }

  compareAddressKey(prevItem, nextItem, index) {
    const { [SEARCH_HISTORY_TAG]: prevValue } = prevItem;
    const { [SEARCH_HISTORY_TAG]: nextValue } = nextItem;
    const theyAreEqual = prevValue === nextValue;

    if (theyAreEqual) {
      this.historyItems.splice(index, 1);
      return true;
    }
  }

  compareFixtureKey(prevItem, nextItem, index) {
    const { [SEARCH_HISTORY_TAG]: prevValue, id: prevId } = prevItem;
    const { [SEARCH_HISTORY_TAG]: nextValue, id: nextId } = nextItem;

    let theyAreEqual = prevId === nextId;

    if (!prevId && !nextId) theyAreEqual = prevValue === nextValue;

    if (theyAreEqual) {
      this.historyItems.splice(index, 1);
      return true;
    }
  }

  /* eslint-disable @typescript-eslint/camelcase */

  getHitType(hit) {
    const { is_city, is_country } = hit;
    if (is_city) return 'city';
    if (is_country) return 'country';
    return 'address';
  }

  /* eslint-enable @typescript-eslint/camelcase */

  addStr(value, del = ', ') {
    if (value) return value + del;
    else return '';
  }

  getHitValue(suggestion) {
    const { country, name, city, administrative } = suggestion;

    return (
      this.addStr(name) +
      this.addStr(city) +
      this.addStr(administrative) +
      this.addStr(country, '')
    );
  }

  /* eslint-disable @typescript-eslint/camelcase */
  parseGeoSuggestion(event, result, dataset, coords) {
    const hit = result.hits[0];

    const {
      country,
      postcode,
      country_code,
      administrative,
      locale_names,
      city,
      county
    } = hit;

    if (hit) {
      const suggestion = {
        country,
        type: this.getHitType(hit),
        name: (locale_names || [])[0],
        postcodes: postcode,
        county: (county || [])[0],
        postcode: (postcode || [])[0],
        city: (city || [])[0],
        latlng: {
          lat: coords.latitude,
          lng: coords.longitude
        },
        countryCode: country_code,
        administrative: (administrative || [])[0]
      };

      suggestion.value = this.getHitValue(suggestion);

      if (!this.locationUpdatePrevented) {
        this.setClass(false, this.spinnerClass);
        this.handleChange(event, suggestion, dataset);
        this.autocompleteElem.blur();
        this.gettingCurrentLocation = false;
      }
    }
  }

  /* eslint-enable @typescript-eslint/camelcase */

  handleCurrentLocationSelect(event, suggestion, dataset) {
    const { _currentLocation } = suggestion;
    const { language } = this.props;

    if (_currentLocation) {
      event.preventDefault();

      if (this.gettingCurrentLocation) return true;

      this.locationUpdatePrevented = false;
      let showLoading = true;

      setTimeout(() => {
        if (showLoading) {
          this.setClass(true, this.spinnerClass);
          this.gettingCurrentLocation = true;
        }
      }, 100);

      const errorCallback = () => {
        showLoading = false;
        this.gettingCurrentLocation = false;
        this.setClass(false, this.spinnerClass);
      };

      if (this.placesInstance) {
        navigator.geolocation.getCurrentPosition((response) => {
          const coords = response.coords;
          const lat = coords.latitude.toFixed(6);
          const lng = coords.longitude.toFixed(6);

          const reverseOptions = {
            aroundLatLng: lat + ',' + lng,
            hitsPerPage: 1,
            language: language
          };

          this.placesInstance.reverse(reverseOptions).then((result) => {
            showLoading = false;
            this.parseGeoSuggestion(event, result, dataset, coords);
          }, errorCallback);
        }, errorCallback);
      }

      return true;
    } else if (this.gettingCurrentLocation) this.stopLocationUpdate();
  }

  stopLocationUpdate() {
    this.setClass(false, this.spinnerClass);
    this.locationUpdatePrevented = true;
    this.gettingCurrentLocation = false;
  }

  executeTimedAction(action, time) {
    if (time) setTimeout(action, time);
    else action();
  }

  setClass(valuePresent, newClass, setTimeout, restoreTimeout) {
    if (!this.containerRef) return;
    const isSet = this.containerRef.classList.contains(newClass);

    if (valuePresent) {
      if (!isSet) {
        this.executeTimedAction(() => {
          this.containerRef.classList.add(newClass);
        }, setTimeout);
      }
    } else {
      if (isSet) {
        this.executeTimedAction(() => {
          this.containerRef.classList.remove(newClass);
        }, restoreTimeout);
      }
    }
  }

  handleInputButton(value) {
    const { closeIconVisible } = this.state;

    if (value.length > 0) {
      if (!closeIconVisible) this.setState({ closeIconVisible: 'visible' });
    } else {
      if (closeIconVisible) this.setState({ closeIconVisible: '' });
    }
  }

  fireOnInputChange(newValue) {
    const { onInputChange } = this.props;
    if (typeof onInputChange === 'function') onInputChange(newValue);
  }

  handleInputChange(event) {
    const { value: newValue } = event.target;
    this.updateInternalValue(newValue);
    this.fireOnInputChange(newValue);
  }

  handleIconClose() {
    this.setState({ closeIconVisible: '' });
    this.updateInputValue('');
    this.fireOnInputChange('');
    this.stopLocationUpdate();
    if (!this.autocompleteElem) return;
    if (this.autocompleteElem !== document.activeElement) this.autocompleteElem.focus();
  }

  handleIconCloseMouseDown() {
    this.iconCloseMouseDown = true;
  }

  handleIconCloseMouseUp() {
    this.iconCloseMouseDown = false;
  }

  handleInputBlur(event) {
    if (this.iconCloseMouseDown) {
      if (!this.autocompleteElem) return;
      event.preventDefault();
      event.stopPropagation();
      this.autocompleteElem.focus();
    }
  }

  updateInputValue(value) {
    if (this.aaInstance && typeof value === 'string') {
      if (value !== this.currentValue) {
        this.updateInternalValue(value);
        this.aaInstance.autocomplete.setVal(value);
      }
    }
  }

  handleValueChange(value) {
    this.handleInputButton(value);
  }

  updateInitialValue(props) {
    const { initialValue } = props;
    const { initialValue: prevValue } = this.props;
    if (
      initialValue &&
      initialValue !== prevValue &&
      initialValue !== this.currentValue
    ) {
      this.updateInputValue(initialValue);
    }
  }

  fuseOptionsUpdated(props) {
    const { fuseOptions } = props;
    const { fuseOptions: prevValue } = this.props;
    return fuseOptions !== prevValue;
  }

  fixturesUpdatedShallow(props) {
    const { fixtures } = props;
    const { fixtures: prevValue } = this.props;
    return fixtures !== prevValue;
  }

  fixturesUpdatedDeep(props) {
    const { fixtures } = props;
    if (this.fixturesUpdatedShallow(props)) return !_isEqual(fixtures, this.prevFixtures);
  }

  fixturesUpdated(props) {
    return this.fixturesUpdatedDeep(props);
  }

  updateFuse(props) {
    const optionsUpdated = this.fuseOptionsUpdated(props);

    if (optionsUpdated) {
      this.setFuseOptions(props);
      this.initFuse(props);
    }
  }

  placeholderUpdated(props) {
    const { placeholder } = props;
    const { placeholder: prevValue } = this.props;
    return placeholder !== prevValue;
  }

  languageUpdated(props) {
    const { language } = props;
    const { language: prevValue } = this.props;
    return language !== prevValue;
  }

  handleHeavyProps(props) {
    if (
      this.languageUpdated(props) ||
      this.fixturesUpdated(props) ||
      this.historyUpdated(props) ||
      this.locationButtonUpdated(props)
    ) {
      this.destroyInstance();
      this.generateInputKey();
      this.initDone = false;
      return true;
    }
    return false;
  }

  lightPropsUpdated(props) {
    return !!this.placeholderUpdated(props);
  }

  handleIndependentProps(props) {
    this.updateInitialValue(props);
    this.updateFuse(props);
  }

  locationButtonUpdated(props) {
    const { showCurrentLocationButton } = props;
    const { showCurrentLocationButton: prevValue } = this.props;
    return showCurrentLocationButton !== prevValue;
  }

  initHistoryFromStorage(props) {
    const { searchHistoryEnabled, searchHistoryId } = props;

    if (window && searchHistoryEnabled && !this.historyItemsLoadedFromLocalStorage) {
      const packedItems = window.localStorage.getItem(searchHistoryId);

      try {
        this.historyItems = JSON.parse(packedItems);
      } catch (e) {
        this.historyItems = [];
      }

      if (!Array.isArray(this.historyItems)) this.historyItems = [];

      this.historyItemsLoadedFromLocalStorage = true;
    }
  }

  historyUpdated(props) {
    const { searchHistoryEnabled } = props;
    const { searchHistoryEnabled: prevValue } = this.props;
    return searchHistoryEnabled !== prevValue;
  }

  destroyInstance() {
    if (this.aaInstance) this.aaInstance.autocomplete.destroy();
  }

  setRef(el) {
    this.containerRef = el;
  }

  render() {
    return (
      <div className={this.containerClass} ref={this.setRef}>
        <input
          key={this.inputKey}
          type="text"
          aria-label={this.props.placeholder}
          placeholder={this.props.placeholder}
          onChange={this.handleInputChange}
          ref={this.setInputRef}
          onBlur={this.handleInputBlur}
        />
        <ReactSVG
          src={closeIcon}
          className={classnames('react-svg', 'close-icon', this.state.closeIconVisible)}
          onClick={this.handleIconClose}
          onMouseDown={this.handleIconCloseMouseDown}
          onMouseLeave={this.handleIconCloseMouseUp}
          onMouseUp={this.handleIconCloseMouseUp}
        />
        <style global jsx>
          {defaultStyle}
        </style>
        <style global jsx>
          {style}
        </style>
        <style global jsx>
          {spinnerStyle}
        </style>
      </div>
    );
  }
}

/*
  This props can be changed dynamically after initialization:

  - initialValue
  - fixtures
  - fixturesTitle
  - fuseOptions
  - language
  - maxFixtures

*/

AlgoliaPlaces.defaultProps = {
  currentLocationLabel: 'Get my location',
  fixturesSearchKeys: ['name', 'address.formattedAddress'],
  fixturesInfoKey: 'address.formattedAddress',
  fixturesDisplayKey: 'name',
  fixturesTitle: 'Sites',
  addressesTitle: 'Addresses',
  historyTitle: 'History',
  maxFixtures: 5,
  maxHistoryItems: 5,
  showCurrentLocationButton: true,
  initialNumberOfFixtures: 0,
  fixturesDatasetName: 'fixtures',
  currentLocationDatasetName: 'current-location',
  historyDatasetName: 'history',
  language: 'en',
  searchHistoryId: 'algolia-search-history',
  searchHistoryEnabled: false,
  fixturesCompareKey: 'id',
  onError: () => undefined,
  onLimit: () => undefined
};

AlgoliaPlaces.propTypes = {
  /** Get function to set input value (have to be used instead of native autocomplete.setVal */
  getValueSetter: PropTypes.func,
  /** https://community.algolia.com/places/documentation.html#options */
  placesOptions: PropTypes.object,
  /** https://github.com/algolia/autocomplete.js/blob/master/README.md#global-options */
  aaOptions: PropTypes.object,
  /** Initial value for input field */
  initialValue: PropTypes.string,
  /** https://community.algolia.com/places/documentation.html#api-options-language */
  language: PropTypes.string,
  /** Custom data to search for */
  fixtures: PropTypes.array,
  /** Maximum number of history items to display (0 - unlimited) */
  maxHistoryItems: PropTypes.number,
  /** Maximum number of fixtures to display (0 - unlimited) */
  maxFixtures: PropTypes.number,
  /** Initial number of fixtures to display (0 - unlimited) */
  initialNumberOfFixtures: PropTypes.number,
  /** Title for provided fixtures */
  fixturesTitle: PropTypes.string,
  /** Title for algolia address results */
  addressesTitle: PropTypes.string,
  /** Title for history results */
  historyTitle: PropTypes.string,
  /** Keys used to filter fixtures (can be lodash path) */
  fixturesSearchKeys: PropTypes.array,
  /** Key to retreive display name of suggestion (can be lodash path) */
  fixturesDisplayKey: PropTypes.string,
  /** Key to display additional info (can be lodash path)  */
  fixturesInfoKey: PropTypes.string,
  /** Input placeholder */
  placeholder: PropTypes.string,
  /** 'Current position' string translated */
  currentLocationLabel: PropTypes.string,
  /** Used for fixtures: https://fusejs.io/ */
  fuseOptions: PropTypes.object,
  /** Autocomplete instance callback */
  aaInstance: PropTypes.func,
  /** Fired when input changes */
  onInputChange: PropTypes.func,
  /** Show current location suggestion */
  showCurrentLocationButton: PropTypes.bool,
  /** Fired when you use arrow keys or cursor to navigate */
  onCursorChanged: PropTypes.func,
  /** Fired when you select the suggestion */
  onSelect: PropTypes.func,
  /** Fired when API limit reached */
  onLimit: PropTypes.func,
  /** Fired if (any other) error (other then API limit) has occured */
  onError: PropTypes.func,
  /** This will be appended to [aa]-dataset-[fixturesDatasetName] class */
  fixturesDatasetName: PropTypes.string,
  /** This will be appended to [aa]-dataset-[currentLocationDatasetName] class */
  currentLocationDatasetName: PropTypes.string,
  /** This will be appended to [aa]-dataset-[historyDatasetName] class */
  historyDatasetName: PropTypes.string,
  /** An id that will be used to store search history in local storage **/
  searchHistoryId: PropTypes.string,
  /** Enable search history **/
  searchHistoryEnabled: PropTypes.bool,
  /** This key is used to remove duplicate fixtures (can be lodash path) **/
  fixturesCompareKey: PropTypes.string
};

export default AlgoliaPlaces;



/** WEBPACK FOOTER **
 ** ./src/components/partials/AlgoliaPlaces/index.js
 **/